import { useEffect, useRef, useState } from 'react';
import { noop } from 'lodash';
import { PopoverAtPoint, ViewportPoint } from '@ardoq/popovers';
import { setIsCurrentlyHoveredImageBroken } from 'tabview/graphComponent/isCurrentlyHoveredImageBroken$';
import { subscribeToAction } from 'streams/utils/streamUtils';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { DEFAULT_ARDOQ_I18NLOCALE, t } from '@ardoq/i18n';
import { useStream } from '@ardoq/rxbeach';

const getPopoverPoint = (imageElement: SVGImageElement): ViewportPoint => {
  const { x, y, width } = imageElement.getBoundingClientRect();
  return { x: x + width / 2, y };
};

/**
 * Shows a popover above a broken image while it is hovered. Zooming or dragging
 * changes the image position and dimensions, so the anchor point is
 * re-evaluated on every mousemove and wheel event until the mouse leaves.
 */
export const BrokenImagePopover = () => {
  const i18nLocale = useStream(i18nLocale$, DEFAULT_ARDOQ_I18NLOCALE);
  const [point, setPoint] = useState<ViewportPoint | null>(null);
  const hoveredImageElementRef = useRef<SVGImageElement | null>(null);

  useEffect(() => {
    const updatePoint = () => {
      const imageElement = hoveredImageElementRef.current;
      if (imageElement) setPoint(getPopoverPoint(imageElement));
    };

    const subscription = subscribeToAction(
      setIsCurrentlyHoveredImageBroken,
      ({ isCurrentlyHoveredImageBroken, imageElement }) => {
        if (!imageElement) return;
        if (isCurrentlyHoveredImageBroken) {
          hoveredImageElementRef.current = imageElement;
          setPoint(getPopoverPoint(imageElement));
          document.addEventListener('mousemove', updatePoint);
          document.addEventListener('mouseup', updatePoint);
          imageElement.addEventListener('wheel', updatePoint, {
            passive: true,
          });
        } else {
          document.removeEventListener('mousemove', updatePoint);
          document.removeEventListener('mouseup', updatePoint);
          imageElement.removeEventListener('wheel', updatePoint);
          hoveredImageElementRef.current = null;
          setPoint(null);
        }
      }
    );

    return () => {
      subscription.unsubscribe();
      document.removeEventListener('mousemove', updatePoint);
      document.removeEventListener('mouseup', updatePoint);
      hoveredImageElementRef.current?.removeEventListener('wheel', updatePoint);
      hoveredImageElementRef.current = null;
    };
  }, []);

  if (!point) return null;

  return (
    <PopoverAtPoint
      point={point}
      isOpen
      // The hover action stream owns this popover's lifecycle (shown while a
      // broken image is hovered, torn down on un-hover), so onClose is a no-op:
      // clearing point here would only flicker — the next mousemove re-opens it.
      onClose={noop}
      placement={{ side: 'top', overflow: 'center' }}
    >
      <div style={{ maxWidth: 350 }}>
        {t(
          i18nLocale,
          'imagePreview:brokenImage.popoverText',
          'Image cannot be displayed'
        )}
      </div>
    </PopoverAtPoint>
  );
};
